/**
 * V2 Drawer — overlay + panel shell.
 * Bottom sheet <900px; right drawer from 900px.
 * Open/close via window.V2Drawer. User dismiss (X / overlay / Escape) fires `v2-drawer:dismiss`.
 */
(function () {
  if (window.V2Drawer) return;

  const ROOT_SELECTOR = '[data-v2-drawer]';
  const OPEN_CLASS = 'is-open';
  const BODY_CLASS = 'v2-drawer-open';

  function setIntercomHidden(hidden) {
    const launcher = document.querySelector('.intercom-launcher');
    if (launcher) {
      launcher.style.display = hidden ? 'none' : '';
    }
    if (typeof window.Intercom === 'function') {
      window.Intercom('update', { hide_default_launcher: hidden });
    }
  }

  function focusables(root) {
    return Array.prototype.slice.call(
      root.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true';
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function closeDurationMs(root) {
    const raw = (window.getComputedStyle(root).getPropertyValue('--v2-drawer-duration') || '0.35s').trim();
    const value = parseFloat(raw);
    if (!value) return 350;
    return raw.indexOf('ms') !== -1 ? value : value * 1000;
  }

  function clearCloseTimer(root) {
    if (!root._v2DrawerCloseTimer) return;
    window.clearTimeout(root._v2DrawerCloseTimer);
    root._v2DrawerCloseTimer = null;
  }

  function open(root) {
    if (!root) return;
    clearCloseTimer(root);
    root._v2DrawerClosing = false;
    root.classList.remove('is-closing');
    root._v2DrawerPrevFocus = document.activeElement;
    root.classList.add(OPEN_CLASS);
    root.removeAttribute('inert');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_CLASS);
    setIntercomHidden(true);

    const closeBtn = root.querySelector('[data-v2-drawer-close]');
    const panel = root.querySelector('.v2-drawer__panel');
    window.requestAnimationFrame(function () {
      const target = closeBtn || panel;
      if (target && typeof target.focus === 'function') target.focus();
    });
  }

  function finishClose(root) {
    if (root.classList.contains(OPEN_CLASS)) return;
    root._v2DrawerClosing = false;
    root.classList.remove('is-closing');
    root.setAttribute('inert', '');
    root.setAttribute('aria-hidden', 'true');

    const stillOpen = document.querySelector(ROOT_SELECTOR + '.' + OPEN_CLASS);
    if (!stillOpen) {
      document.body.classList.remove(BODY_CLASS);
      setIntercomHidden(false);
    }

    const prev = root._v2DrawerPrevFocus;
    if (prev && typeof prev.focus === 'function') prev.focus();
    root._v2DrawerPrevFocus = null;
  }

  function close(root) {
    if (!root || root._v2DrawerClosing) return;
    if (!root.classList.contains(OPEN_CLASS)) return;

    if (prefersReducedMotion()) {
      finishClose(root);
      return;
    }

    root._v2DrawerClosing = true;
    root.classList.add('is-closing');
    root.classList.remove(OPEN_CLASS);

    const panel = root.querySelector('.v2-drawer__panel');
    let finished = false;
    const done = function () {
      if (finished) return;
      finished = true;
      clearCloseTimer(root);
      if (panel) panel.removeEventListener('transitionend', onEnd);
      finishClose(root);
    };
    const onEnd = function (event) {
      if (event.target !== panel) return;
      if (event.propertyName && event.propertyName !== 'transform') return;
      done();
    };
    if (panel) panel.addEventListener('transitionend', onEnd);
    root._v2DrawerCloseTimer = window.setTimeout(done, closeDurationMs(root) + 50);
  }

  function dismiss(root) {
    if (!root || !root.classList.contains(OPEN_CLASS)) return;
    root.dispatchEvent(
      new CustomEvent('v2-drawer:dismiss', { bubbles: true, detail: { root: root } })
    );
  }

  window.V2Drawer = {
    open: open,
    close: close,
  };

  document.addEventListener('click', function (event) {
    const closeBtn = event.target.closest('[data-v2-drawer-close]');
    if (closeBtn) {
      event.preventDefault();
      dismiss(closeBtn.closest(ROOT_SELECTOR));
      return;
    }

    const overlay = event.target.closest('[data-v2-drawer-overlay]');
    if (overlay) {
      dismiss(overlay.closest(ROOT_SELECTOR));
    }
  });

  document.addEventListener('keydown', function (event) {
    const openDrawers = document.querySelectorAll(ROOT_SELECTOR + '.' + OPEN_CLASS);
    if (!openDrawers.length) return;
    const root = openDrawers[openDrawers.length - 1];

    if (event.key === 'Escape') {
      event.preventDefault();
      dismiss(root);
      return;
    }

    if (event.key !== 'Tab') return;

    const items = focusables(root);
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
